// ============================================
// AUTO CLOSURE ADMIN PAGE
// Configuration et gestion des fermetures automatiques
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
    Clock, Settings, Save, RefreshCw, AlertTriangle, 
    CheckCircle, Play, Bell, Calendar, Globe,
    Shield, Eye, Edit, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const TIMEZONES = [
    { value: 'Africa/Libreville', label: 'Gabon (Libreville) - UTC+1' },
    { value: 'Africa/Lagos', label: 'Nigeria (Lagos) - UTC+1' },
    { value: 'Africa/Douala', label: 'Cameroun (Douala) - UTC+1' },
    { value: 'Europe/Paris', label: 'France (Paris) - UTC+1/+2' },
    { value: 'UTC', label: 'UTC' }
];

const DAYS_OF_WEEK = [
    { value: 0, label: 'Lundi' },
    { value: 1, label: 'Mardi' },
    { value: 2, label: 'Mercredi' },
    { value: 3, label: 'Jeudi' },
    { value: 4, label: 'Vendredi' },
    { value: 5, label: 'Samedi' },
    { value: 6, label: 'Dimanche' }
];

const AutoClosureAdminPage = () => {
    const { api, user } = useAuth();
    const [settings, setSettings] = useState({
        timezone: 'Africa/Libreville',
        reminder_time: '20:00',
        warning_time: '20:25',
        auto_close_time: '20:30',
        enabled: true,
        working_days: [0, 1, 2, 3, 4, 5, 6],
        notification_sound: 'alert'
    });
    const [status, setStatus] = useState(null);
    const [unverifiedClosures, setUnverifiedClosures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [verifyingId, setVerifyingId] = useState(null);
    const [verifyData, setVerifyData] = useState({ actual_cash: '', notes: '' });

    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            const [settingsRes, statusRes, unverifiedRes] = await Promise.all([
                api().get('/auto-closure/settings').catch(() => ({ data: settings })),
                api().get('/auto-closure/status'),
                api().get('/auto-closure/unverified')
            ]);
            
            setSettings(settingsRes.data);
            setStatus(statusRes.data);
            setUnverifiedClosures(unverifiedRes.data.closures || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Save settings
    const saveSettings = async () => {
        setSaving(true);
        try {
            await api().put('/auto-closure/settings', settings);
            toast.success('Paramètres enregistrés');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    // Toggle working day
    const toggleWorkingDay = (day) => {
        const days = [...settings.working_days];
        const index = days.indexOf(day);
        if (index > -1) {
            days.splice(index, 1);
        } else {
            days.push(day);
            days.sort((a, b) => a - b);
        }
        setSettings({ ...settings, working_days: days });
    };

    // Manual trigger
    const triggerAction = async (action) => {
        try {
            const response = await api().post('/auto-closure/trigger', { action });
            toast.success(`Action "${action}" exécutée: ${response.data.message || 'OK'}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    // Verify closure
    const verifyClosure = async (closureId) => {
        if (!verifyData.actual_cash) {
            toast.error('Veuillez saisir le montant réel en caisse');
            return;
        }

        try {
            await api().post(`/auto-closure/verify/${closureId}`, {
                actual_cash: parseFloat(verifyData.actual_cash),
                notes: verifyData.notes
            });
            toast.success('Clôture vérifiée');
            setVerifyingId(null);
            setVerifyData({ actual_cash: '', notes: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
            </div>
        );
    }

    // Check if user is Admin
    if (user?.role !== 'ADMIN') {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                    <Shield className="w-16 h-16 mx-auto mb-4 text-red-400" />
                    <h1 className="text-2xl font-bold mb-2">Accès refusé</h1>
                    <p className="text-gray-400">Seul l'administrateur peut accéder à cette page</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-blue-500/20">
                            <Clock className="w-8 h-8 text-blue-400" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Fermeture Automatique des Caisses</h1>
                            <p className="text-gray-400">Configuration et gestion des rappels</p>
                        </div>
                    </div>
                    <Button onClick={fetchData} variant="outline" size="sm">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                </div>

                {/* Current Status */}
                <div className="grid md:grid-cols-3 gap-4">
                    <div className={`p-6 rounded-2xl border ${
                        status?.open_count > 0 
                            ? 'bg-amber-500/10 border-amber-500/30' 
                            : 'bg-emerald-500/10 border-emerald-500/30'
                    }`}>
                        <div className="flex items-center gap-3 mb-2">
                            {status?.open_count > 0 ? (
                                <AlertTriangle className="w-6 h-6 text-amber-400" />
                            ) : (
                                <CheckCircle className="w-6 h-6 text-emerald-400" />
                            )}
                            <span className="font-semibold">Caisses ouvertes</span>
                        </div>
                        <p className="text-3xl font-bold">{status?.open_count || 0}</p>
                        {status?.open_registers?.length > 0 && (
                            <div className="mt-2 text-sm text-gray-400">
                                {status.open_registers.map(r => r.register_name).join(', ')}
                            </div>
                        )}
                    </div>

                    <div className={`p-6 rounded-2xl border ${
                        unverifiedClosures.length > 0 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : 'bg-gray-800 border-gray-700'
                    }`}>
                        <div className="flex items-center gap-3 mb-2">
                            <Eye className="w-6 h-6 text-red-400" />
                            <span className="font-semibold">À vérifier</span>
                        </div>
                        <p className="text-3xl font-bold">{unverifiedClosures.length}</p>
                        <p className="text-sm text-gray-400">Fermetures automatiques non vérifiées</p>
                    </div>

                    <div className="p-6 rounded-2xl border bg-gray-800 border-gray-700">
                        <div className="flex items-center gap-3 mb-2">
                            <Globe className="w-6 h-6 text-blue-400" />
                            <span className="font-semibold">Heure actuelle</span>
                        </div>
                        <p className="text-3xl font-bold">{status?.current_time || '--:--'}</p>
                        <p className="text-sm text-gray-400">{settings.timezone}</p>
                    </div>
                </div>

                {/* Settings */}
                <div className="p-6 rounded-2xl border bg-gray-800/60 border-gray-700">
                    <div className="flex items-center gap-3 mb-6">
                        <Settings className="w-6 h-6 text-gray-400" />
                        <h2 className="text-xl font-semibold">Paramètres</h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Enable/Disable */}
                        <div className="col-span-2">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={settings.enabled}
                                    onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                                    className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                                />
                                <span className="font-medium">Activer la fermeture automatique</span>
                            </label>
                        </div>

                        {/* Timezone */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Fuseau horaire</label>
                            <select
                                value={settings.timezone}
                                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                            >
                                {TIMEZONES.map(tz => (
                                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Reminder Time */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                <Bell className="w-4 h-4 inline mr-2" />
                                Heure du rappel
                            </label>
                            <input
                                type="time"
                                value={settings.reminder_time}
                                onChange={(e) => setSettings({ ...settings, reminder_time: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                            />
                        </div>

                        {/* Warning Time */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                <AlertTriangle className="w-4 h-4 inline mr-2" />
                                Heure d'alerte
                            </label>
                            <input
                                type="time"
                                value={settings.warning_time}
                                onChange={(e) => setSettings({ ...settings, warning_time: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                            />
                        </div>

                        {/* Auto-close Time */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                <Clock className="w-4 h-4 inline mr-2" />
                                Heure de fermeture auto
                            </label>
                            <input
                                type="time"
                                value={settings.auto_close_time}
                                onChange={(e) => setSettings({ ...settings, auto_close_time: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                            />
                        </div>

                        {/* Working Days */}
                        <div className="col-span-2">
                            <label className="block text-sm font-medium mb-3">
                                <Calendar className="w-4 h-4 inline mr-2" />
                                Jours de fonctionnement
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {DAYS_OF_WEEK.map(day => (
                                    <button
                                        key={day.value}
                                        onClick={() => toggleWorkingDay(day.value)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                            settings.working_days.includes(day.value)
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                                        }`}
                                    >
                                        {day.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="mt-6 pt-6 border-t border-gray-700 flex justify-end">
                        <Button onClick={saveSettings} disabled={saving}>
                            {saving ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Enregistrer
                        </Button>
                    </div>
                </div>

                {/* Manual Triggers (for testing) */}
                <div className="p-6 rounded-2xl border bg-gray-800/60 border-gray-700">
                    <h2 className="text-xl font-semibold mb-4">Déclenchement manuel (Test)</h2>
                    <p className="text-sm text-gray-400 mb-4">
                        Utilisez ces boutons pour tester le système manuellement
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Button onClick={() => triggerAction('reminder')} variant="outline">
                            <Bell className="w-4 h-4 mr-2" />
                            Rappel (20h)
                        </Button>
                        <Button onClick={() => triggerAction('warning')} variant="outline" className="border-amber-500 text-amber-500 hover:bg-amber-500/10">
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Alerte (20h25)
                        </Button>
                        <Button onClick={() => triggerAction('auto_close')} variant="outline" className="border-red-500 text-red-500 hover:bg-red-500/10">
                            <Clock className="w-4 h-4 mr-2" />
                            Fermeture auto (20h30)
                        </Button>
                    </div>
                </div>

                {/* Unverified Closures */}
                {unverifiedClosures.length > 0 && (
                    <div className="p-6 rounded-2xl border bg-red-500/10 border-red-500/30">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                            <h2 className="text-xl font-semibold">Clôtures à vérifier</h2>
                        </div>
                        
                        <div className="space-y-4">
                            {unverifiedClosures.map(closure => (
                                <div key={closure.id} className="p-4 rounded-xl bg-gray-800/50 border border-gray-700">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <span className="font-semibold">{closure.register_name}</span>
                                            <span className="text-gray-400 ml-2">({closure.user_name})</span>
                                        </div>
                                        <span className="text-sm text-gray-400">{closure.local_time}</span>
                                    </div>
                                    
                                    <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                                        <div>
                                            <p className="text-gray-400">Total théorique</p>
                                            <p className="font-semibold">{closure.theoretical_total?.toLocaleString()} F</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-400">Espèces théoriques</p>
                                            <p className="font-semibold">{closure.theoretical_cash?.toLocaleString()} F</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-400">Commandes</p>
                                            <p className="font-semibold">{closure.orders_count}</p>
                                        </div>
                                    </div>

                                    {verifyingId === closure.id ? (
                                        <div className="space-y-3 border-t border-gray-700 pt-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Montant réel en caisse (F)</label>
                                                <input
                                                    type="number"
                                                    value={verifyData.actual_cash}
                                                    onChange={(e) => setVerifyData({ ...verifyData, actual_cash: e.target.value })}
                                                    className="w-full px-4 py-2 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                                                    placeholder="Ex: 150000"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
                                                <input
                                                    type="text"
                                                    value={verifyData.notes}
                                                    onChange={(e) => setVerifyData({ ...verifyData, notes: e.target.value })}
                                                    className="w-full px-4 py-2 rounded-lg bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none"
                                                    placeholder="Observations..."
                                                />
                                            </div>
                                            <div className="flex gap-2">
                                                <Button onClick={() => verifyClosure(closure.id)} size="sm">
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                    Valider
                                                </Button>
                                                <Button 
                                                    onClick={() => { setVerifyingId(null); setVerifyData({ actual_cash: '', notes: '' }); }} 
                                                    variant="outline" 
                                                    size="sm"
                                                >
                                                    <X className="w-4 h-4 mr-2" />
                                                    Annuler
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <Button 
                                            onClick={() => { setVerifyingId(closure.id); setVerifyData({ actual_cash: closure.theoretical_cash || '', notes: '' }); }}
                                            size="sm"
                                        >
                                            <Edit className="w-4 h-4 mr-2" />
                                            Vérifier cette clôture
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AutoClosureAdminPage;
